import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Link as LinkIcon, ChevronDown, ChevronRight,
  CheckCircle2, ExternalLink, Search, Ban, Plus, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { RESEARCH } from "@/constants/testIds";
import ExtraSourcesEditor from "@/components/ExtraSourcesEditor";
import SourceLimitNote from "@/components/SourceLimitNote";
import { useWriteQueue } from "@/lib/writeQueue";
import { readAutoCheckPref, writeAutoCheckPref } from "@/lib/autoFactCheck";
import SourceDateTag from "@/components/SourceDateTag";
import {
  fullMessage, overflowUrls, partialAddMessage, slotsLeft,
  takeWithinLimit, uniqueUrls,
} from "@/lib/sourceLimit";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

const CATEGORY_COLORS = {
  breaking: "bg-red-50 text-red-700 border-red-200",
  crime: "bg-zinc-100 text-zinc-800 border-zinc-300",
  politics: "bg-amber-50 text-amber-800 border-amber-200",
  business: "bg-emerald-50 text-emerald-800 border-emerald-200",
  real_estate: "bg-emerald-50 text-emerald-800 border-emerald-200",
  culture: "bg-orange-50 text-orange-800 border-orange-200",
  events: "bg-sky-50 text-sky-800 border-sky-200",
  food: "bg-yellow-50 text-yellow-800 border-yellow-200",
  environment: "bg-teal-50 text-teal-800 border-teal-200",
  travel: "bg-blue-50 text-blue-800 border-blue-200",
  human_interest: "bg-purple-50 text-purple-800 border-purple-200",
  service: "bg-slate-100 text-slate-800 border-slate-300",
};

function CategoryBadge({ category }) {
  const cls =
    CATEGORY_COLORS[category] || "bg-slate-100 text-slate-800 border-slate-300";
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${cls} font-mono-display`}
    >
      {category?.replace(/_/g, " ")}
    </span>
  );
}

/**
 * Renders a list of ideas for a research run.
 * - Each idea has its own writer <select>, prefilled with recommended_writer.
 * - Ideas that already have article_id show a "Written" badge and a link.
 * - Multi-select checkboxes for batch generation.
 * - "Find more sources" returns SUGGESTIONS (a dashed picker under the
 *   sources list); nothing lands on the idea until the editor clicks
 *   Add, which persists via POST /ideas/{id}/add-sources. Same UX as
 *   the custom-article page. (Before 2026-09-13 the backend appended
 *   every hit pre-ticked and the editor had to uncheck the rejects.)
 *
 * Props:
 *   `research` — the loaded research doc (with .ideas array).
 *   `writers`  — writer list for the dropdown.
 *   `onResearchStale` — OPTIONAL callback. Fired when a mutation has
 *     changed the underlying research doc (e.g. add-sources persisted
 *     new URLs). The parent should refetch its `research` prop. When
 *     absent, we still call `refetchQueries(["research", research.id])`
 *     — which works for pages that register that key (e.g. RunsPage)
 *     but is a no-op on pages that hold `research` in local state
 *     (e.g. ResearchPage). Passing `onResearchStale` on ResearchPage
 *     is what closes the "added source vanishes" gap.
 */
export default function IdeasList({ research, writers, onResearchStale }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  // ideaId -> writer_key override
  const [writerOverrides, setWriterOverrides] = useState({});
  // ideaId -> custom prompt text
  const [customPrompts, setCustomPrompts] = useState({});
  // ideaId -> Set<url> of sources the editor has DESELECTED for this idea
  const [excludedSources, setExcludedSources] = useState({});
  // ideaId -> string[] of URLs the editor manually added
  const [extraSources, setExtraSources] = useState({});
  const [findingIds, setFindingIds] = useState(new Set());
  // ideaId -> array of newly-added URLs (for the confirmation flash)
  const [newSourceFlash, setNewSourceFlash] = useState({});
  // ideaId -> [{url, title, source, snippet, needs_decode}] returned by
  // "Find more sources". These are SUGGESTIONS only — nothing is on the
  // idea until the editor clicks Add (which goes through add-sources).
  // Mirrors `suggestedSources` on CustomArticlePage.
  const [suggestedSources, setSuggestedSources] = useState({});
  // URLs currently being persisted via Add, so the row can't be
  // double-clicked while the request is in flight.
  const [addingUrls, setAddingUrls] = useState(new Set());
  // Local set of headlines the editor has just added to the do-not-cover
  // list, so the button flips to a confirmed state without a full refetch.
  const [banned, setBanned] = useState(new Set());
  const [banningIds, setBanningIds] = useState(new Set());
  // "Fact check after writing" — remembered per browser (2026-10-01).
  // Each article is checked on the server while the next one is being
  // written; results show in the write-progress floater.
  const [autoCheck, setAutoCheck] = useState(() => readAutoCheckPref());
  const toggleAutoCheck = (value) => {
    setAutoCheck(value);
    writeAutoCheckPref(value);
  };
  // Batch write state now lives in the global WriteQueueProvider so
  // progress survives page navigation. This component just reads
  // whether a given idea is being written to keep row spinners live
  // while the editor is on this page.
  const {
    startBatch,
    isGenerating: isIdeaGenerating,
    active: batchActive,
    items: batchItems,
  } = useWriteQueue();
  // Number of ideas from this batch still in-flight (pending or
  // writing). Drives the top-of-page "Writing N…" button label.
  const inflightCount = batchActive
    ? batchItems.filter(
        (i) => i.status === "pending" || i.status === "writing",
      ).length
    : 0;

  // Current do-not-cover list is stored on the property's settings doc.
  // Fetch it so we can merge-append when the editor adds a new entry.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  const ideas = research?.ideas || [];
  const writerFor = (idea) =>
    writerOverrides[idea.id] || idea.recommended_writer || "archer_ingram";

  // The URLs that will actually be sent for this idea, in the order
  // routes/articles.py builds them: the idea's checked sources, then
  // any staged manual extras not already on the idea. Only the first
  // 8 reach the writer (lib/sourceLimit.js), so every Add path checks
  // against this list.
  const sendListFor = (idea) => {
    if (!idea) return [];
    const excluded = excludedSources[idea.id] || new Set();
    const checked = (idea.source_urls || []).filter((u) => !excluded.has(u));
    return uniqueUrls([...checked, ...(extraSources[idea.id] || [])]);
  };
  // Suggestions for this idea whose Add request is still in flight.
  // Counted against the limit so two quick clicks can't both take the
  // last slot.
  const inFlightFor = (ideaId) =>
    (suggestedSources[ideaId] || [])
      .map((s) => s.url)
      .filter((u) => addingUrls.has(u));

  // Watch-and-prune: once a locally-staged extra source shows up in
  // its idea's server-persisted `source_urls`, drop it from
  // `extraSources` so it isn't rendered twice (once in the main
  // "Sources · N" list, once in the extras input). This is the
  // BACKSTOP for the "added source vanishes" bug: even if the parent
  // never refetches, the URL stays in `extraSources` until we can
  // safely hand it off to the main list. Prevents a URL from being
  // rendered nowhere.
  useEffect(() => {
    setExtraSources((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const idea of ideas) {
        const staged = prev[idea.id];
        if (!staged || staged.length === 0) continue;
        const persisted = new Set(idea.source_urls || []);
        const stillStaged = staged.filter((u) => !persisted.has(u));
        if (stillStaged.length !== staged.length) {
          next[idea.id] = stillStaged;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [ideas]);

  const toggleSelected = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleExpanded = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const selectedCount = selected.size;

  const handleGenerate = async () => {
    const ids = Array.from(selected).filter((id) => {
      const idea = ideas.find((i) => i.id === id);
      return idea && !idea.article_id;
    });
    if (ids.length === 0) {
      toast.error(tr("Select at least one unwritten idea"));
      return;
    }

    // Build the batch entries once — the WriteQueueProvider owns the
    // async loop so it survives navigation away from the run page.
    const entries = ids.map((id) => {
      const idea = ideas.find((i) => i.id === id);
      const wk = writerFor(idea);
      const cp = (customPrompts[id] || "").trim();
      const excluded = Array.from(excludedSources[id] || []);
      const extra = extraSources[id] || [];
      return {
        ideaId: id,
        headline: idea?.headline_draft || "",
        params: {
          idea_id: id,
          writer_key: wk,
          ...(cp ? { custom_prompt: cp } : {}),
          ...(excluded.length ? { excluded_source_urls: excluded } : {}),
          ...(extra.length ? { additional_source_urls: extra } : {}),
          ...(autoCheck ? { auto_fact_check: true } : {}),
        },
      };
    });

    // Clear selection immediately so the UI reflects the "handed off"
    // state — the floater takes over showing per-item progress.
    setSelected(new Set());
    await startBatch(entries);
    // After the batch settles, re-fetch the run so ideas show as
    // written (article_id) and any newly-inserted "Live" badges show up.
    qc.invalidateQueries({ queryKey: ["research", research.id] });
  };

  const handleFindMore = async (ideaId) => {
    setFindingIds((prev) => new Set(prev).add(ideaId));
    // Auto-expand so the user sees the suggestions panel appear
    setExpanded((prev) => new Set(prev).add(ideaId));
    try {
      const r = await api.findMoreSources(ideaId);
      // Suggest, don't persist: the backend no longer appends these
      // to `idea.source_urls`. Show them in a picker and let the
      // editor Add the ones they want — same UX as the custom-article
      // page. Any earlier, un-added suggestions for this idea are
      // replaced by the fresh set.
      const items = r.new_sources || [];
      setSuggestedSources((prev) => ({ ...prev, [ideaId]: items }));
      const stats = r.decode_stats || {};
      // "Decoder engaged but everything it tried failed" — this used
      // to render as "No new sources found — try refining the angle",
      // which sent editors on a wild-goose chase for a working angle
      // when the actual problem was Google News rate-limiting the
      // decoder. Now we say what's really happening.
      const decoderTotallyBroken =
        (stats.attempted || 0) > 0 && (stats.decoded || 0) === 0;
      if ((r.new_source_urls || []).length === 0) {
        if (decoderTotallyBroken) {
          toast.error(
            tr("Source lookup is failing to resolve Google News links — check server logs"),
            { duration: 8000 },
          );
        } else {
          toast.message(tr("No new sources found — try refining the angle"));
        }
      } else {
        toast.success(
          tr("Found {itemCount} source{s} — pick the ones to add", { itemCount: items.length, s: items.length === 1 ? "" : "s" })
        );
        // Secondary, non-blocking notice: some winners survived
        // ranking but couldn't be decoded. They're still in the
        // list, flagged `needs_decode: true` for later retry.
        if ((stats.failed || 0) > 0) {
          toast.message(
            tr("{failed} source{s} could not be fully resolved", { failed: stats.failed, s: stats.failed === 1 ? "" : "s" }),
            { duration: 6000 },
          );
        }
      }
      // No refetch here: the idea itself hasn't changed. The refetch
      // happens per-Add in `handleAddSuggested`.
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || tr("Find more sources failed")
      );
    } finally {
      setFindingIds((prev) => {
        const next = new Set(prev);
        next.delete(ideaId);
        return next;
      });
    }
  };

  // Persist one or more suggested URLs onto the idea. Shares the
  // add-sources → flash → refetch-both-channels pattern with the
  // ExtraSourcesEditor path below; see the comments there for why the
  // refetch goes through BOTH the query cache and `onResearchStale`.
  const handleAddSuggested = async (ideaId, urls) => {
    const requested = (urls || []).filter((u) => u && !addingUrls.has(u));
    if (requested.length === 0) return;
    // Source limit: URLs already on the idea cost nothing (the backend
    // dedupes them and they just leave the picker); new ones only go
    // in while there are free slots. Anything that doesn't fit STAYS
    // in the picker so the editor can uncheck a source and try again.
    const idea = ideas.find((i) => i.id === ideaId);
    const onIdea = new Set(idea?.source_urls || []);
    const fresh = requested.filter((u) => !onIdea.has(u));
    const { accepted, rejected } = takeWithinLimit(
      fresh, [...sendListFor(idea), ...inFlightFor(ideaId)],
    );
    if (fresh.length > 0 && accepted.length === 0) {
      toast.error(fullMessage());
      return;
    }
    const toAdd = [...requested.filter((u) => onIdea.has(u)), ...accepted];
    setAddingUrls((prev) => {
      const s = new Set(prev);
      toAdd.forEach((u) => s.add(u));
      return s;
    });
    try {
      const r = await api.addSourcesToIdea(ideaId, toAdd);
      const added = r.new_source_urls || [];
      // Drop every URL we sent from the picker — including ones the
      // backend deduped as already present, which would otherwise
      // linger as an Add button that does nothing.
      setSuggestedSources((prev) => ({
        ...prev,
        [ideaId]: (prev[ideaId] || []).filter((s) => !toAdd.includes(s.url)),
      }));
      if (added.length) {
        setNewSourceFlash((prev) => ({
          ...prev,
          [ideaId]: [...(prev[ideaId] || []), ...added],
        }));
        setTimeout(() => {
          setNewSourceFlash((prev) => ({
            ...prev,
            [ideaId]: (prev[ideaId] || []).filter((u) => !added.includes(u)),
          }));
        }, 5000);
        if (rejected.length > 0) {
          toast.message(partialAddMessage(added.length, rejected.length), {
            duration: 6000,
          });
        } else {
          toast.success(
            added.length === 1
              ? tr("Source added")
              : tr("{addedCount} sources added", { addedCount: added.length }),
          );
        }
      } else {
        toast.message(tr("Already on this idea's source list"));
      }
      await qc.refetchQueries({ queryKey: ["research", research.id] });
      qc.invalidateQueries({ queryKey: ["past-runs"] });
      if (typeof onResearchStale === "function") {
        try {
          await onResearchStale();
        } catch (cbErr) {
          // eslint-disable-next-line no-console
          console.warn("onResearchStale failed", cbErr);
        }
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || tr("Failed to add source"),
      );
    } finally {
      setAddingUrls((prev) => {
        const s = new Set(prev);
        toAdd.forEach((u) => s.delete(u));
        return s;
      });
    }
  };

  const handleDoNotCover = async (idea) => {
    const title = (idea.headline_draft || "").trim();
    if (!title) return;
    setBanningIds((prev) => new Set(prev).add(idea.id));
    try {
      // Merge-append: replace-semantics on the backend means we must send
      // the full list. Dedupe case-insensitively against the existing pool.
      const current = Array.isArray(settings?.do_not_cover)
        ? settings.do_not_cover
        : [];
      const lower = new Set(current.map((s) => s.toLowerCase()));
      if (lower.has(title.toLowerCase())) {
        setBanned((prev) => new Set(prev).add(idea.id));
        toast.message(tr("Already on the Do-not-cover list"));
        return;
      }
      const next = [...current, title];
      await api.updateSettings({ do_not_cover: next });
      setBanned((prev) => new Set(prev).add(idea.id));
      // Also deselect it if the editor had ticked the row.
      setSelected((prev) => {
        const s = new Set(prev);
        s.delete(idea.id);
        return s;
      });
      toast.success(tr("Added to Do-not-cover list — future runs will skip it"));
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || tr("Could not update the list"),
      );
    } finally {
      setBanningIds((prev) => {
        const next = new Set(prev);
        next.delete(idea.id);
        return next;
      });
    }
  };

  if (ideas.length === 0) {
    return (
      <div className="bg-white border border-[var(--border)] py-16 text-center">
        <div className="font-serif-display text-2xl mb-2">{tr("No ideas in this run")}</div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="overline">{tr("Story ideas · {ideaCount}", { ideaCount: ideas.length })}</div>
          <div className="text-xs text-[var(--ink-muted)] mt-1">{tr("Sampled {raw_source_count} raw items · {today}", { raw_source_count: research.raw_source_count, today: research.today })}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <label
            className="inline-flex items-center gap-2 text-xs text-[var(--ink)] cursor-pointer select-none"
            title={tr("Each article is fact-checked as soon as it's written, while the next one is being written. Results appear in the progress panel and in each article's Fact check section.")}
          >
            <input
              type="checkbox"
              data-testid="research-auto-fact-check"
              checked={autoCheck}
              onChange={(e) => toggleAutoCheck(e.target.checked)}
              disabled={inflightCount > 0}
              className="accent-[var(--accent)]"
            />{tr("Fact check after writing")}</label>
          <button
            data-testid={RESEARCH.generateSelectedButton}
            data-ai="true"
            onClick={handleGenerate}
            disabled={selectedCount === 0 || inflightCount > 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {inflightCount > 0 ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />{tr("Writing {inflightCount}…", { inflightCount })}</>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />{tr("Write {selectedCount} selected", { selectedCount: selectedCount || "" })}</>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--border)]">
        {ideas.map((idea, i) => {
          const written = !!idea.article_id;
          const isSel = selected.has(idea.id);
          const isExp = expanded.has(idea.id);
          const isGen = isIdeaGenerating(idea.id);
          return (
            <div
              key={idea.id}
              data-testid={RESEARCH.ideaRow}
              className={`border-b border-[var(--border)] last:border-b-0 ${
                written
                  ? "bg-emerald-50/30"
                  : isSel
                    ? "bg-orange-50/40"
                    : "bg-white"
              }`}
            >
              <div className="flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4">
                <input
                  type="checkbox"
                  data-testid={`${RESEARCH.ideaCheckbox}-${i}`}
                  checked={isSel}
                  disabled={written}
                  onChange={() => toggleSelected(idea.id)}
                  className="mt-1 w-4 h-4 accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 shrink-0"
                />
                <button
                  onClick={() => toggleExpanded(idea.id)}
                  className="mt-0.5 text-[var(--ink-muted)] hover:text-[var(--ink)] shrink-0"
                  aria-label={tr("expand")}
                >
                  {isExp ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
                    <h3 className="font-serif-display text-base sm:text-lg leading-snug flex-1 min-w-[60%]">
                      {idea.headline_draft}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      {isGen && (
                        <span
                          data-testid={`idea-generating-${idea.id}`}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-amber-50 border border-amber-300 text-amber-800 font-mono-display"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />{tr("Generating")}</span>
                      )}
                      <CategoryBadge category={idea.category} />
                    </div>
                  </div>
                  <p className="text-sm text-[var(--ink-muted)] mt-1.5 leading-relaxed">
                    {idea.angle}
                  </p>

                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    {written ? (
                      <button
                        onClick={() => navigate(`/article/${idea.article_id}`)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-100 border border-emerald-300 text-emerald-900 hover:bg-emerald-200"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />{tr("Article written — Open")}<ExternalLink className="w-3 h-3" />
                      </button>
                    ) : (
                      <>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">{tr("Writer")}</label>
                        <select
                          data-testid={`${RESEARCH.ideaWriterSelect}-${i}`}
                          value={writerFor(idea)}
                          onChange={(e) =>
                            setWriterOverrides((prev) => ({
                              ...prev,
                              [idea.id]: e.target.value,
                            }))
                          }
                          className="px-2 py-1 border border-[var(--border)] bg-white text-xs outline-none focus:border-[var(--slate-900)]"
                        >
                          {writers.map((w) => (
                            <option key={w.key} value={w.key}>
                              {w.name}
                              {w.key === idea.recommended_writer
                                ? tr(" (recommended)")
                                : ""}
                            </option>
                          ))}
                        </select>
                      </>
                    )}

                    <button
                      data-testid={`${RESEARCH.ideaFindMoreButton}-${i}`}
                      onClick={() => handleFindMore(idea.id)}
                      disabled={findingIds.has(idea.id)}
                      title={tr("Search the web for more sources on this topic")}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {findingIds.has(idea.id) ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />{tr("Scouring the web…")}</>
                      ) : (
                        <>
                          <Search className="w-3 h-3" />{tr("Find more sources")}</>
                      )}
                    </button>

                    {!written && (
                      <button
                        data-testid={`idea-do-not-cover-${i}`}
                        onClick={() => handleDoNotCover(idea)}
                        disabled={
                          banningIds.has(idea.id) || banned.has(idea.id)
                        }
                        title={tr("Add this story to the Do-not-cover list so it won't be suggested again")}
                        className={
                          "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border disabled:cursor-not-allowed " +
                          (banned.has(idea.id)
                            ? "border-red-300 bg-red-50 text-red-800 opacity-90"
                            : "border-[var(--border)] hover:border-red-300 hover:bg-red-50 hover:text-red-800")
                        }
                      >
                        {banningIds.has(idea.id) ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />{tr("Adding…")}</>
                        ) : banned.has(idea.id) ? (
                          <>
                            <Ban className="w-3 h-3" />{tr("Won’t suggest again")}</>
                        ) : (
                          <>
                            <Ban className="w-3 h-3" />{tr("Do not cover")}</>
                        )}
                      </button>
                    )}
                  </div>

                  {!written && (
                    <div className="mt-3">
                      <label
                        htmlFor={`custom-prompt-${idea.id}`}
                        className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1"
                      >{trRich("Add to prompt<0>— optional angles or points the writer must cover</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1.5 font-sans" />])}
                      </label>
                      <textarea
                        id={`custom-prompt-${idea.id}`}
                        data-testid={`idea-custom-prompt-${i}`}
                        value={customPrompts[idea.id] || ""}
                        onChange={(e) =>
                          setCustomPrompts((prev) => ({
                            ...prev,
                            [idea.id]: e.target.value,
                          }))
                        }
                        rows={2}
                        maxLength={2000}
                        placeholder={tr("e.g. Cover the implications of a governor holding a US green card. Ask why serve Mexico if you want to live in the US.")}
                        className="w-full text-sm bg-white border border-[var(--border)] px-2 py-1.5 focus:outline-none focus:border-[var(--slate-900)] resize-y"
                      />
                    </div>
                  )}

                  {isExp && (
                    <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-2 text-xs">
                      {idea.novelty_note && (
                        <div>
                          {trRich("<0>Why fresh</0>", null, [<span className="overline" />])}
                          <div className="text-[var(--ink)] mt-1">
                            {idea.novelty_note}
                          </div>
                        </div>
                      )}
                      {idea.source_urls?.length > 0 && (
                        <div>
                          {(() => {
                            const excluded = excludedSources[idea.id] || new Set();
                            const activeCount =
                              idea.source_urls.length - excluded.size;
                            return (
                              <span className="overline">{tr("Sources · {activeCount}/{source_urlCount}", { activeCount, source_urlCount: idea.source_urls.length })}
                                {excluded.size > 0 && (
                                  <span className="normal-case tracking-normal text-[var(--ink-muted)] font-sans ml-2">{tr("({size} skipped)", { size: excluded.size })}</span>
                                )}
                              </span>
                            );
                          })()}
                          <ul className="mt-1 space-y-1">
                            {idea.source_urls.map((u, j) => {
                              const isNew = (
                                newSourceFlash[idea.id] || []
                              ).includes(u);
                              const excluded = excludedSources[idea.id] || new Set();
                              const isExcluded = excluded.has(u);
                              const isOverflow =
                                !isExcluded &&
                                overflowUrls(sendListFor(idea)).includes(u);
                              return (
                                <li
                                  key={j}
                                  className="flex items-start gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    data-testid={`source-checkbox-${idea.id}-${j}`}
                                    checked={!isExcluded}
                                    disabled={written}
                                    onChange={() => {
                                      // Re-checking a skipped source uses
                                      // a slot — refuse it when full.
                                      if (
                                        isExcluded &&
                                        slotsLeft(sendListFor(idea).length) === 0
                                      ) {
                                        toast.error(fullMessage());
                                        return;
                                      }
                                      setExcludedSources((prev) => {
                                        const cur = new Set(prev[idea.id] || []);
                                        cur.has(u) ? cur.delete(u) : cur.add(u);
                                        return { ...prev, [idea.id]: cur };
                                      });
                                    }}
                                    title={
                                      isExcluded
                                        ? tr("Skipped — click to include this source")
                                        : tr("Included — click to skip this source")
                                    }
                                    className="mt-0.5 w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 shrink-0"
                                  />
                                  <LinkIcon
                                    className={
                                      "w-3 h-3 mt-0.5 shrink-0 " +
                                      (isExcluded
                                        ? "text-[var(--ink-muted)]/50"
                                        : "text-[var(--ink-muted)]")
                                    }
                                  />
                                  <a
                                    href={u}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={
                                      "break-all underline underline-offset-2 " +
                                      (isExcluded
                                        ? "text-[var(--ink-muted)] line-through decoration-[var(--ink-muted)]/50"
                                        : isNew
                                          ? "text-emerald-700 hover:text-emerald-800"
                                          : "text-[var(--accent)] hover:text-[var(--accent-hover)]")
                                    }
                                  >
                                    {u}
                                  </a>
                                  {isNew && (
                                    <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-emerald-100 text-emerald-800 font-mono-display shrink-0">{tr("new")}</span>
                                  )}
                                  {isOverflow && (
                                    <span
                                      data-testid={`source-overflow-${idea.id}-${j}`}
                                      title={tr("Past the 8-source limit — the writer won't receive this one")}
                                      className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-mono-display shrink-0"
                                    >{tr("not sent")}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      {(suggestedSources[idea.id] || []).length > 0 && (() => {
                        const sugg = suggestedSources[idea.id];
                        const anyAdding = sugg.some((s) => addingUrls.has(s.url));
                        const free = slotsLeft(
                          uniqueUrls([...sendListFor(idea), ...inFlightFor(idea.id)]).length,
                        );
                        const onIdea = new Set(idea.source_urls || []);
                        return (
                          <div
                            data-testid={`idea-suggested-sources-${idea.id}`}
                            className="mt-2 border border-dashed border-[var(--border)] p-2.5 bg-white"
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              {trRich("<0>Suggested sources · {suggCount}<1>— not on the idea until you add them · {value}</1></0>", { suggCount: sugg.length, value: free === 0
                                    ? tr("source limit reached")
                                    : tr("room for {free} more", { free }) }, [<span className="overline text-[var(--ink-muted)]" />, <span className="normal-case tracking-normal font-sans ml-1.5" />])}
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  data-testid={`idea-suggested-add-all-${idea.id}`}
                                  onClick={() =>
                                    handleAddSuggested(idea.id, sugg.map((s) => s.url))
                                  }
                                  disabled={anyAdding || free === 0}
                                  title={
                                    free === 0
                                      ? fullMessage()
                                      : free < sugg.length
                                        ? tr("Adds the first {free} — the writer only takes 8 sources", { free })
                                        : tr("Add every suggestion")
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Plus className="w-3 h-3" />
                                  {free > 0 && free < sugg.length ? tr("Add {free}", { free }) : tr("Add all")}
                                </button>
                                <button
                                  data-testid={`idea-suggested-clear-${idea.id}`}
                                  onClick={() =>
                                    setSuggestedSources((prev) => ({
                                      ...prev,
                                      [idea.id]: [],
                                    }))
                                  }
                                  disabled={anyAdding}
                                  title={tr("Dismiss these suggestions")}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-[var(--border)] text-[var(--ink-muted)] hover:border-[var(--ink)] hover:text-[var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <X className="w-3 h-3" />{tr("Clear")}</button>
                              </div>
                            </div>
                            <ul className="space-y-1.5">
                              {sugg.map((s, k) => {
                                const isAdding = addingUrls.has(s.url);
                                const blocked = free === 0 && !onIdea.has(s.url);
                                return (
                                  <li
                                    key={s.url}
                                    data-testid={`idea-suggested-source-${idea.id}-${k}`}
                                    className="flex items-start gap-2"
                                  >
                                    <button
                                      data-testid={`idea-suggested-add-${idea.id}-${k}`}
                                      onClick={() => handleAddSuggested(idea.id, [s.url])}
                                      disabled={isAdding || blocked}
                                      title={blocked ? fullMessage() : undefined}
                                      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isAdding ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Plus className="w-3 h-3" />
                                      )}{tr("Add")}</button>
                                    <div className="flex-1 min-w-0">
                                      <a
                                        href={s.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block break-words text-[var(--ink)] hover:text-[var(--accent)]"
                                      >
                                        {s.title || s.url}
                                      </a>
                                      <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                                        {s.source ? `${s.source} · ` : ""}
                                        <SourceDateTag
                                          publishedAt={s.published_at}
                                          testId={`idea-suggested-date-${idea.id}-${k}`}
                                        />
                                        {s.needs_decode ? tr(" · unresolved Google News link") : ""}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })()}
                      {!written && (
                        <div className="pt-2 space-y-2">
                          <SourceLimitNote
                            count={sendListFor(idea).length}
                            testId={`idea-source-limit-${idea.id}`}
                          />
                          <ExtraSourcesEditor
                            urls={extraSources[idea.id] || []}
                            maxUrls={
                              (extraSources[idea.id] || []).length +
                              slotsLeft(sendListFor(idea).length)
                            }
                            onChange={(next) => {
                              const prev = extraSources[idea.id] || [];
                              const added = next.filter((u) => !prev.includes(u));
                              if (added.length > 0) {
                                // Persist ADDS immediately so a failed
                                // article generation or a page reload
                                // doesn't lose the editor's curated
                                // URLs. The local extras entry stays
                                // visible until we OBSERVE the URL
                                // arrive in `idea.source_urls` (see the
                                // `useEffect` further up in this
                                // component) — that watch-and-prune
                                // pattern means the URL is always
                                // rendered somewhere, even if the
                                // parent's refetch is delayed or the
                                // parent doesn't use a query cache.
                                setExtraSources((p) => ({
                                  ...p,
                                  [idea.id]: next,
                                }));
                                (async () => {
                                  try {
                                    const r = await api.addSourcesToIdea(
                                      idea.id, added
                                    );
                                    if (r.new_source_urls?.length) {
                                      setNewSourceFlash((prev2) => ({
                                        ...prev2,
                                        [idea.id]: [
                                          ...(prev2[idea.id] || []),
                                          ...r.new_source_urls,
                                        ],
                                      }));
                                      setTimeout(() => {
                                        setNewSourceFlash((prev2) => ({
                                          ...prev2,
                                          [idea.id]: (prev2[idea.id] || [])
                                            .filter((u) => !r.new_source_urls.includes(u)),
                                        }));
                                      }, 5000);
                                    }
                                    // Refresh the underlying research
                                    // doc via BOTH channels — the
                                    // query cache (works on RunsPage)
                                    // AND the optional parent callback
                                    // (works on ResearchPage, which
                                    // holds research in local state).
                                    // Whichever channel the host page
                                    // supports will fire; the other is
                                    // a harmless no-op.
                                    await qc.refetchQueries({
                                      queryKey: ["research", research.id],
                                    });
                                    if (typeof onResearchStale === "function") {
                                      try {
                                        await onResearchStale();
                                      } catch (cbErr) {
                                        // Parent callback failure is
                                        // non-fatal — the URL is still
                                        // safely persisted server-side
                                        // and staged locally.
                                        // eslint-disable-next-line no-console
                                        console.warn(
                                          "onResearchStale failed", cbErr,
                                        );
                                      }
                                    }
                                    // NOTE: we DO NOT clear from
                                    // `extraSources` here. The
                                    // watch-and-prune effect at the
                                    // top of this component handles
                                    // that once the URL actually shows
                                    // up in `idea.source_urls`. This
                                    // is the fix for the "added source
                                    // vanishes" race: previously the
                                    // clear ran unconditionally and
                                    // any refresh failure (or no-op
                                    // refetch on ResearchPage) left
                                    // the URL rendered nowhere.
                                  } catch (e) {
                                    // On failure, roll back the local
                                    // staging so the URL doesn't look
                                    // like it stuck when it didn't.
                                    setExtraSources((p) => ({
                                      ...p,
                                      [idea.id]: (p[idea.id] || [])
                                        .filter((u) => !added.includes(u)),
                                    }));
                                    toast.error(
                                      e?.response?.data?.detail ||
                                        tr("Failed to save source — try again")
                                    );
                                  }
                                })();
                              } else {
                                // Pure remove (local only, not yet
                                // persisted) — just update state.
                                setExtraSources((p) => ({
                                  ...p,
                                  [idea.id]: next,
                                }));
                              }
                            }}
                            testIdPrefix={`idea-extra-source-${idea.id}`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mirror of the top "Write selected" button. Shows only when
          there's a scroll-worth of ideas AND at least one is
          selected — no point offering a duplicate CTA on an empty
          selection while the top button is already visible. */}
      {ideas.length >= 5 && selectedCount > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            data-testid={`${RESEARCH.generateSelectedButton}-bottom`}
            data-ai="true"
            onClick={handleGenerate}
            disabled={inflightCount > 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {inflightCount > 0 ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />{tr("Writing {inflightCount}…", { inflightCount })}</>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />{tr("Write {selectedCount} selected", { selectedCount: selectedCount || "" })}</>
            )}
          </button>
        </div>
      )}
    </>
  );
}
